import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO, getWebPageSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function ProcessusPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.21daybusinesswebsite.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(
        'Les 3 semaines - Processus de création',
        'Découvrez notre processus clair et efficace pour créer votre site business en 3 semaines : stratégie, design et lancement.',
        `${siteUrl}/processus`
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Processus', url: '/processus' }
      ]),
      {
        '@type': 'HowTo',
        name: 'Comment créer un site web business en 21 jours',
        description: 'Processus en 3 étapes pour créer un site web professionnel',
        step: [
          {
            '@type': 'HowToStep',
            name: 'Semaine 1 - Stratégie & Structure',
            text: 'Définition du positionnement, messages clés, structure du site et maquettes filaires'
          },
          {
            '@type': 'HowToStep',
            name: 'Semaine 2 - Design & Contenu',
            text: 'Création du design sur mesure, identité visuelle et rédaction des 15 pages'
          },
          {
            '@type': 'HowToStep',
            name: 'Semaine 3 - Intégration & Lancement',
            text: 'Développement technique, optimisation SEO, tests UX et mise en ligne'
          }
        ]
      }
    ]
  };

  useSEO({
    title: 'Les 3 semaines - Processus de création | THE 21-DAY BUSINESS WEBSITE™',
    description: 'Découvrez notre processus clair et efficace pour créer votre site business en 3 semaines : stratégie, design et lancement.',
    keywords: 'processus création site web, méthodologie web, étapes création site, agence web processus',
    canonical: `${siteUrl}/processus`,
    schema
  });

  const weeks = [
    {
      number: '1',
      title: 'Semaine 1',
      subtitle: 'Stratégie & Structure',
      icon: 'ri-lightbulb-line',
      color: 'from-yellow-600 to-yellow-700',
      steps: [
        {
          title: 'Positionnement',
          description: 'Nous définissons votre positionnement unique et ce qui vous différencie de vos concurrents.'
        },
        {
          title: 'Messages clés',
          description: 'Nous identifions les messages essentiels qui vont résonner avec votre audience cible.'
        },
        {
          title: 'Structure du site',
          description: 'Nous créons l\'architecture de votre site pour une navigation optimale et une conversion maximale.'
        },
        {
          title: 'Maquettes filaires',
          description: 'Nous concevons les maquettes de chaque page pour valider la structure avant le design.'
        }
      ]
    },
    {
      number: '2',
      title: 'Semaine 2',
      subtitle: 'Design & Contenu',
      icon: 'ri-palette-line',
      color: 'from-yellow-700 to-amber-600',
      steps: [
        {
          title: 'Design sur mesure',
          description: 'Création d\'un design unique qui reflète votre identité et inspire confiance.'
        },
        {
          title: 'Identité minimale',
          description: 'Développement d\'une identité visuelle cohérente (couleurs, typographies, style).'
        },
        {
          title: 'Rédaction des 15 pages',
          description: 'Rédaction professionnelle de tout le contenu de votre site, optimisé pour la conversion et le SEO.'
        },
        {
          title: 'Validation',
          description: 'Vous validez le design et le contenu. Si besoin, nous ajustons jusqu\'à satisfaction complète.'
        }
      ]
    },
    {
      number: '3',
      title: 'Semaine 3',
      subtitle: 'Intégration & Lancement',
      icon: 'ri-rocket-line',
      color: 'from-amber-600 to-yellow-600',
      steps: [
        {
          title: 'Intégration',
          description: 'Développement technique de votre site avec les meilleures pratiques du web.'
        },
        {
          title: 'SEO',
          description: 'Optimisation complète pour les moteurs de recherche (balises, vitesse, structure).'
        },
        {
          title: 'Tests UX',
          description: 'Tests approfondis sur tous les appareils pour garantir une expérience parfaite.'
        },
        {
          title: 'Mise en ligne',
          description: 'Lancement de votre site + formation pour que vous puissiez le gérer en autonomie.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Les <span className="text-yellow-500">3 semaines</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Un processus clair, efficace et transparent pour créer votre site business
            </p>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="space-y-24">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="relative">
                  {/* Week Header */}
                  <div className="flex items-center mb-12">
                    <div className={`w-20 h-20 flex items-center justify-center bg-gradient-to-br ${week.color} rounded-2xl shadow-xl flex-shrink-0`}>
                      <i className={`${week.icon} text-4xl text-white`}></i>
                    </div>
                    <div className="ml-6">
                      <div className="text-sm font-semibold text-yellow-700 mb-1">{week.title}</div>
                      <h2 className="text-4xl font-bold text-slate-900">{week.subtitle}</h2>
                    </div>
                  </div>

                  {/* Steps Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ml-0 md:ml-26">
                    {week.steps.map((step, stepIndex) => (
                      <div 
                        key={stepIndex}
                        className="bg-slate-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-yellow-600/20"
                      >
                        <div className="flex items-start space-x-4">
                          <div className="w-8 h-8 flex items-center justify-center bg-yellow-600 rounded-lg flex-shrink-0">
                            <span className="text-white font-bold text-sm">{stepIndex + 1}</span>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-2">
                              {step.title}
                            </h3>
                            <p className="text-slate-600 leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why 3 Weeks */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Pourquoi 3 semaines ?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-2xl mx-auto mb-6">
                  <i className="ri-speed-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Rapidité
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Assez rapide pour ne pas perdre de temps, assez long pour garantir la qualité. Le timing parfait.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-2xl mx-auto mb-6">
                  <i className="ri-focus-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Efficacité
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Un processus optimisé où chaque semaine a un objectif clair. Pas de temps perdu, que du concret.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-2xl mx-auto mb-6">
                  <i className="ri-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Qualité
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Suffisamment de temps pour créer un site premium, sur mesure, qui reflète vraiment votre expertise.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-gradient-to-br from-yellow-600 to-yellow-700 text-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à démarrer ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Votre site peut être en ligne dans 3 semaines
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" className="inline-block px-8 py-4 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer">
                Démarrer mon projet
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link to="/offre" className="inline-block px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white hover:text-yellow-700 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Voir l'offre
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
